import {
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  useCallback, useContext, useEffect, useState,
} from 'react';
import CloseButton from '../Buttons/Close';
import BlockData from '../../models/BlockData';
import BlockType from '../../models/BlockType';
import { URLOpenType } from '../../models/URLOpenType';
import { URLTargetType } from '../../models/URLTargetType';
import LinkData from '../../models/LinkData';
import styles from './LinkComponent.module.css';
import EditableText from '../Form/EditableText';
import { PagesContext } from '../../contexts/PagesContext';
import { FileContext, FileInterface } from '../../contexts/FileContext';

interface Props {
  link: LinkData;
  blockData: BlockData[];
  firstLoad: boolean;
  hideRemoveButton?: boolean;
  hideTitle?: boolean;
  onRemoveBlock?: () => void;
  onChangeURLTitle?: (title: string) => void;
  onChangeLink?: (link: string) => void;
  onSelectBlock?: (selected: string) => void;
  onSelectUrl?: (selected: string) => void;
  onChangeTargetType?: (target: any) => void;
  onChangeOpenType?: (checked: boolean) => void;
  onChangeFile: (file: FileInterface) => void;
}

function LinkComponent({
  link,
  blockData,
  firstLoad,
  hideRemoveButton = false,
  hideTitle = false,
  onRemoveBlock,
  onChangeURLTitle,
  onChangeLink,
  onSelectBlock,
  onSelectUrl,
  onChangeTargetType,
  onChangeOpenType,
  onChangeFile,
}: Props) {
  const [radioBtnClassInternal, setRadioBtnClassInternal] = useState<string>('');
  const [radioBtnClassExternal, setRadioBtnClassExternal] = useState<string>('');
  const [radioBtnClassCustom, setRadioBtnClassCustom] = useState<string>('');
  const [radioBtnClassFile, setRadioBtnClassFile] = useState<string>('');
  const [startingTargetType] = useState<URLTargetType>(link.linkData.targetType);
  const [text, setText] = useState<string>(link.title);
  const { getPageSubpages } = useContext(PagesContext);
  const { files } = useContext(FileContext);

  useEffect(() => {
    setText(link.title);
  }, [link.title]);

  const urlOptions = getPageSubpages().map((subpage) => (
    // PDEV3-34: page reference is user content — keep untranslated (placeholder still translates)
    <MenuItem value={`/${subpage.url_path}`} key={subpage.id} translate="no" className="notranslate">
      {`https://${subpage.custom_url}/${subpage.url_path} (${subpage.subpage_title})`}
    </MenuItem>
  ));

  const blockOptions = blockData.map((block, index) => (
    // PDEV3-34: block reference is user content — keep untranslated (placeholder still translates)
    <MenuItem value={`#${block.id}`} key={block.id} translate="no" className="notranslate">{`Block ${index}: ${BlockType.describe(block.type)} (${block.id})`}</MenuItem>
  ));

  const matchBlockId = useCallback(
    (url?: string) => blockData.some(((item) => `#${item.id}` === url)),
    [blockData],
  );

  const uniqueName = `radio-${Date.now()}`;

  const changeRadioButtonClass = useCallback(() => {
    if (link.linkData.targetType === URLTargetType.INTERNAL) {
      setRadioBtnClassInternal(styles.dRadioLabelChecked);
      setRadioBtnClassExternal('');
      setRadioBtnClassCustom('');
      setRadioBtnClassFile('');
    } else if (link.linkData.targetType === URLTargetType.EXTERNAL) {
      setRadioBtnClassInternal('');
      setRadioBtnClassExternal(styles.dRadioLabelChecked);
      setRadioBtnClassCustom('');
      setRadioBtnClassFile('');
    } else if (link.linkData.targetType === URLTargetType.CUSTOM_URL) {
      setRadioBtnClassInternal('');
      setRadioBtnClassExternal('');
      setRadioBtnClassCustom(styles.dRadioLabelChecked);
      setRadioBtnClassFile('');
    } else {
      setRadioBtnClassInternal('');
      setRadioBtnClassExternal('');
      setRadioBtnClassCustom('');
      setRadioBtnClassFile(styles.dRadioLabelChecked);
    }
  }, [link.linkData.targetType]);

  const getMatchingSubpage = (url: string) => {
    const subpage = getPageSubpages().find((sub) => url === `/${sub.url_path}`);
    return subpage ? `/${subpage?.url_path}` : '';
  };

  const getMatchingFile = (filePath: string) => {
    if (filePath.includes('files')) {
      const fileName = filePath.split('/').pop();
      const file = files.find((f) => f.file_name === fileName);
      if (!file) return '';
      return file.id;
    }

    const file = files.find((f) => f.path === filePath);
    if (!file) return '';
    return file.id;
  };

  useEffect(() => {
    changeRadioButtonClass();
  }, [link, link.linkData, changeRadioButtonClass]);

  useEffect(() => {
    if (firstLoad && startingTargetType === URLTargetType.CUSTOM_URL) {
      const subpage = getPageSubpages().find((sub) => link.linkData.url === `/${sub.url_path}`);
      if (!subpage) {
        onChangeTargetType(URLTargetType.EXTERNAL);
      }
    }
  });

  return (
    <>
      {!hideRemoveButton && <CloseButton updateHandler={onRemoveBlock} />}
      {!hideTitle && (
        <div>
          <Typography variant="subtitle2" className="text-bold" color="#495057">
            Label
          </Typography>
          <div className="editable-text-container">
            <EditableText
              text={text}
              isEditable
              updateHandler={(value) => {
                setText(value);
                onChangeURLTitle?.(value);
              }}
            />
          </div>
        </div>
      )}

      <div className="d-flex align-items-center gap-2 mb-2 mt-1">
        <div className="d-radio-group">
          <div className="d-radio h-100">
            <label className={`radio-primary h-100 ${radioBtnClassInternal}`}>
              <input
                type="radio"
                checked={link.linkData.targetType === URLTargetType.INTERNAL}
                onChange={() => {
                  onChangeTargetType(URLTargetType.INTERNAL);
                  changeRadioButtonClass();
                }}
                name={uniqueName}
              />
              Jump to Block
            </label>
          </div>
          <div className="d-radio h-100">
            <label className={`radio-primary h-100 ${radioBtnClassExternal}`}>
              <input
                type="radio"
                checked={link.linkData.targetType === URLTargetType.EXTERNAL}
                onChange={() => {
                  onChangeLink('');
                  onChangeTargetType(URLTargetType.EXTERNAL);
                  changeRadioButtonClass();
                }}
                name={uniqueName}
              />
              Direct URL
            </label>
          </div>
          <div className="d-radio h-100">
            <label className={`radio-primary h-100 ${radioBtnClassFile}`}>
              <input
                type="radio"
                checked={link.linkData.targetType === URLTargetType.FILE}
                onChange={() => {
                  onChangeLink('');
                  onChangeTargetType(URLTargetType.FILE);
                  changeRadioButtonClass();
                }}
                name={uniqueName}
              />
              File Link
            </label>
          </div>
          {urlOptions.length > 0 && (
            <div className="d-radio h-100">
              <label className={`radio-primary h-100 ${radioBtnClassCustom}`}>
                <input
                  type="radio"
                  checked={link.linkData.targetType === URLTargetType.CUSTOM_URL}
                  onChange={() => {
                    onChangeLink('');
                    onChangeTargetType(URLTargetType.CUSTOM_URL);
                    changeRadioButtonClass();
                  }}
                  name={uniqueName}
                />
                Site link
              </label>
            </div>
          )}
        </div>
        <div className={styles.stdCheckbox}>
          <label>
            <input
              type="checkbox"
              checked={link.linkData.openType === URLOpenType.NEW_TAB}
              onChange={(event) => onChangeOpenType(event.target.checked)}
              name="sliderlink--out"
            />
            Open in new tab
          </label>
        </div>
      </div>

      {link.linkData.targetType === URLTargetType.INTERNAL
        && (
          <>
            <FormControl size="small" fullWidth>
              <Select
                value={matchBlockId(link.linkData.url) ? link.linkData.url : ''}
                onChange={(event) => onSelectBlock(event.target.value)}
                displayEmpty
              >
                <MenuItem value="" key="">
                  <em>Select a block...</em>
                </MenuItem>
                {blockOptions}
              </Select>
            </FormControl>
          </>
        )}

      {(link.linkData.targetType === URLTargetType.EXTERNAL)
        && (
          <>
            <TextField
              required
              sx={{ fontSize: '.875rem' }}
              label="Link"
              placeholder="https://..."
              InputLabelProps={{ shrink: true }}
              defaultValue={link.linkData.url}
              onChange={(event) => onChangeLink(event.target.value)}
              size="small"
              fullWidth
            />
          </>
        )}

      {link.linkData.targetType === URLTargetType.FILE
        && (
          <>
            <FormControl size="small" fullWidth>
              {files.length === 0 ? (
                <div className="m-1">
                  No files have been uploaded
                </div>
              ) : (
                <Select
                  value={getMatchingFile(link.linkData.url)}
                  onChange={(event) => onChangeFile(files.find((file) => file.id === Number(event.target.value)))}
                  displayEmpty
                >
                  <MenuItem value="" key="">
                    <em>No file uploaded</em>
                  </MenuItem>
                  {files.map((file) => (
                    <MenuItem value={file.id} key={file.id} translate="no" className="notranslate">
                      {file.file_name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FormControl>
          </>
        )}

      {link.linkData.targetType === URLTargetType.CUSTOM_URL
        && (
          <>
            <FormControl size="small" fullWidth>
              {urlOptions.length === 0 ? (
                <div className="mr-5">
                  No site pages have been configured
                </div>
              ) : (
                <Select
                  value={getMatchingSubpage(link.linkData.url)}
                  onChange={(event) => onSelectUrl(event.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" key="">
                    <em>Please select</em>
                  </MenuItem>
                  {urlOptions}
                </Select>
              )}
            </FormControl>
          </>
        )}
    </>
  );
}

export default LinkComponent;
